import type {
  Bookmaker,
  Market,
  FixtureOddsResponse,
  PreferredOddsResponse,
  UserOddsPreferences,
  FixtureTimeSplitContext,
  CardsContextResponse,
} from './types';

const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? 'http://localhost:8005/v1';

// Token getter function - set by useApiClient hook when Clerk is available
let getAuthToken: (() => Promise<string | null>) | null = null;

// Auth ready promise - resolves when token getter is set up (Phase 12f)
let authReadyResolve: (() => void) | null = null;
const authReadyPromise = new Promise<void>((resolve) => {
  authReadyResolve = resolve;
});

/**
 * Wait for auth token getter to be set up.
 * Replaces hardcoded 300ms setTimeout delays in contexts.
 */
export function waitForAuthReady(): Promise<void> {
  return authReadyPromise;
}

export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  getAuthToken = getter;
  // Signal that auth is ready
  if (authReadyResolve) {
    authReadyResolve();
    authReadyResolve = null;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json() as Promise<T>;
}

// Auth-aware API helper for GET requests (uses Clerk token)
export async function apiGetAuth<T>(path: string): Promise<T> {
  const token = getAuthToken ? await getAuthToken() : null;
  console.log(`[apiGetAuth] ${path} - token: ${token ? 'present (' + token.substring(0, 20) + '...)' : 'null'}`);
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  console.log(`[apiGetAuth] ${path} - headers:`, headers);
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json() as Promise<T>;
}

// Auth-aware API helper for PUT requests (uses Clerk token)
export async function apiPutAuth<T>(path: string, body: unknown): Promise<T> {
  const token = getAuthToken ? await getAuthToken() : null;
  console.log(`[apiPutAuth] ${path} - token: ${token ? 'present (' + token.substring(0, 20) + '...)' : 'null'}`);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  console.log(`[apiPutAuth] ${path} - headers:`, Object.keys(headers));
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    console.error(`[apiPutAuth] ${path} - failed with status ${res.status}`);
    // Try to extract error detail from response body
    try {
      const errorData = await res.json();
      throw new Error(errorData.detail || `API error ${res.status}`);
    } catch (parseError) {
      if (parseError instanceof Error && parseError.message !== `API error ${res.status}`) {
        throw parseError;
      }
      throw new Error(`API error ${res.status}`);
    }
  }
  return res.json() as Promise<T>;
}

// Auth-aware API helper for POST requests (uses Clerk token)
export async function apiPostAuth<T>(path: string, body: unknown): Promise<T> {
  const token = getAuthToken ? await getAuthToken() : null;
  console.log(`[apiPostAuth] ${path} - token: ${token ? 'present (' + token.substring(0, 20) + '...)' : 'null'}`);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  console.log(`[apiPostAuth] ${path} - headers:`, Object.keys(headers));
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    console.error(`[apiPostAuth] ${path} - failed with status ${res.status}`);
    // Try to extract error detail from response body
    try {
      const errorData = await res.json();
      throw new Error(errorData.detail || `API error ${res.status}`);
    } catch (parseError) {
      if (parseError instanceof Error && parseError.message !== `API error ${res.status}`) {
        throw parseError;
      }
      throw new Error(`API error ${res.status}`);
    }
  }
  return res.json() as Promise<T>;
}

// ============================================================
// Odds API Functions
// ============================================================

export async function getBookmakers(): Promise<Bookmaker[]> {
  return apiGet<Bookmaker[]>('/odds/bookmakers');
}

export async function getMarkets(): Promise<Market[]> {
  return apiGet<Market[]>('/odds/markets');
}

export async function getFixtureOdds(
  fixtureId: number,
  market?: string
): Promise<FixtureOddsResponse> {
  const params = market ? `?market=${market}` : '';
  return apiGet<FixtureOddsResponse>(`/odds/fixtures/${fixtureId}${params}`);
}

export async function getFixtureOddsPreferred(
  fixtureId: number
): Promise<PreferredOddsResponse> {
  return apiGetAuth<PreferredOddsResponse>(`/odds/fixtures/${fixtureId}/preferred`);
}

export async function getUserOddsPreferences(): Promise<UserOddsPreferences> {
  return apiGetAuth<UserOddsPreferences>('/odds/preferences');
}

export async function updateUserOddsPreferences(
  prefs: Partial<{
    preferred_bookmaker_slug: string;
    show_all_bookmakers: boolean;
    odds_format: string;
  }>
): Promise<UserOddsPreferences> {
  return apiPutAuth<UserOddsPreferences>('/odds/preferences', prefs);
}

// Dropping odds API
export interface DroppingOddsItem {
  fixture_id: number;
  fixture_label: string;
  kickoff_utc: string;
  league_id: number;
  market_slug: string;
  market_name: string;
  bookmaker_slug: string;
  bookmaker_name: string;
  outcome: string;
  line: number | null;
  player_name: string | null;
  current_odds: number;
  previous_odds: number;
  drop_pct: number;
  dropped_from_time: string;
  updated_at: string;
  selection_label: string;
}

export interface DroppingOddsResponse {
  items: DroppingOddsItem[];
  total_count: number;
}

export async function getDroppingOdds(params?: {
  hours_back?: number;
  min_drop_pct?: number;
  league_ids?: number[];
  fixture_id?: number;
  limit?: number;
}): Promise<DroppingOddsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.hours_back) searchParams.set('hours_back', String(params.hours_back));
  if (params?.min_drop_pct) searchParams.set('min_drop_pct', String(params.min_drop_pct));
  if (params?.league_ids?.length) searchParams.set('league_ids', params.league_ids.join(','));
  if (params?.fixture_id) searchParams.set('fixture_id', String(params.fixture_id));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  return apiGet<DroppingOddsResponse>(`/fixtures/dropping-odds${query ? `?${query}` : ''}`);
}

// Auth-aware API helper for DELETE requests (uses Clerk token)
export async function apiDeleteAuth(path: string): Promise<void> {
  const token = getAuthToken ? await getAuthToken() : null;
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
}

// Auth-aware API helper for PATCH requests (uses Clerk token)
export async function apiPatchAuth<T>(path: string, body: unknown): Promise<T> {
  const token = getAuthToken ? await getAuthToken() : null;
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json() as Promise<T>;
}

// ============================================================
// User Data API Functions (Phase 4)
// ============================================================

export interface TrackedBetSelection {
  id?: number;
  fixture_id?: number;
  fixture_name: string;
  title: string;
  line?: number;
  market_type: string;
  scope: 'team' | 'player' | 'match';
  team_type: 'home' | 'away' | 'total';
  is_over: boolean;
  confidence?: 'low' | 'med' | 'high';
  confidence_pct?: number;
  odds?: number;
  outcome?: 'won' | 'lost' | 'void';
  actual_value?: number;
}

export interface TrackedBetAPI {
  id: number;
  odds_decimal: number | null;
  stake: number | null;
  currency: string;
  status: 'pending' | 'won' | 'lost' | 'void' | 'cashout';
  placed_at: string;
  settled_at: string | null;
  bookmaker: string | null;
  notes: string | null;
  selections: TrackedBetSelection[];
  created_at: string;
  updated_at: string;
}

export interface TrackedBetCreatePayload {
  selections: Omit<TrackedBetSelection, 'id' | 'outcome' | 'actual_value'>[];
  odds_decimal?: number;
  stake?: number;
  currency?: string;
  placed_at: string;
  bookmaker?: string;
  notes?: string;
}

export interface BetStatistics {
  total_bets: number;
  pending_bets: number;
  won_bets: number;
  lost_bets: number;
  void_bets: number;
  total_staked: number;
  total_returns: number;
  profit_loss: number;
  roi_percent: number | null;
  win_rate_percent: number | null;
  average_odds: number | null;
}

export async function getTrackedBets(status?: string): Promise<TrackedBetAPI[]> {
  const params = status ? `?status=${status}` : '';
  return apiGetAuth<TrackedBetAPI[]>(`/user/bets${params}`);
}

export async function createTrackedBet(data: TrackedBetCreatePayload): Promise<TrackedBetAPI> {
  return apiPostAuth<TrackedBetAPI>('/user/bets', data);
}

export async function getTrackedBet(id: number): Promise<TrackedBetAPI> {
  return apiGetAuth<TrackedBetAPI>(`/user/bets/${id}`);
}

export async function updateTrackedBet(id: number, data: Partial<TrackedBetAPI>): Promise<TrackedBetAPI> {
  return apiPatchAuth<TrackedBetAPI>(`/user/bets/${id}`, data);
}

export async function settleTrackedBet(id: number, status: 'won' | 'lost' | 'void'): Promise<TrackedBetAPI> {
  return apiPostAuth<TrackedBetAPI>(`/user/bets/${id}/settle`, { status });
}

export async function deleteTrackedBet(id: number): Promise<void> {
  return apiDeleteAuth(`/user/bets/${id}`);
}

export async function getBetStatistics(): Promise<BetStatistics> {
  return apiGetAuth<BetStatistics>('/user/stats');
}

// Player shortlists
export interface PlayerShortlistItem {
  id: number;
  player_id: number;
  player_name: string | null;
  team_name: string | null;
  notes: string | null;
  created_at: string;
}

export async function getPlayerShortlist(): Promise<PlayerShortlistItem[]> {
  return apiGetAuth<PlayerShortlistItem[]>('/user/shortlists');
}

export async function addToPlayerShortlist(player_id: number, notes?: string): Promise<PlayerShortlistItem> {
  return apiPostAuth<PlayerShortlistItem>('/user/shortlists', { player_id, notes });
}

export async function removeFromPlayerShortlist(shortlist_id: number): Promise<void> {
  return apiDeleteAuth(`/user/shortlists/${shortlist_id}`);
}

export async function removePlayerFromShortlist(player_id: number): Promise<void> {
  return apiDeleteAuth(`/user/shortlists/player/${player_id}`);
}

// Saved builders
export interface SavedBuilderAPI {
  id: number;
  fixture_id: number;
  name: string | null;
  selections_json: string;
  created_at: string;
  updated_at: string;
}

export interface InvalidatedPlayerInfo {
  player_id: number;
  player_name: string;
  selection_title: string;
  status: string;
  reason: string | null;
}

export interface SavedBuilderWithStatusAPI extends SavedBuilderAPI {
  is_invalid: boolean;
  invalidated_players: InvalidatedPlayerInfo[];
}

export async function getSavedBuilders(fixture_id?: number): Promise<SavedBuilderAPI[]> {
  const params = fixture_id ? `?fixture_id=${fixture_id}` : '';
  return apiGetAuth<SavedBuilderAPI[]>(`/user/builders${params}`);
}

export async function getSavedBuildersWithStatus(fixture_id?: number): Promise<SavedBuilderWithStatusAPI[]> {
  const params = fixture_id ? `?fixture_id=${fixture_id}` : '';
  return apiGetAuth<SavedBuilderWithStatusAPI[]>(`/user/builders/with-status${params}`);
}

export async function createSavedBuilder(fixture_id: number, selections_json: string, name?: string): Promise<SavedBuilderAPI> {
  return apiPostAuth<SavedBuilderAPI>('/user/builders', { fixture_id, selections_json, name });
}

export async function deleteSavedBuilder(id: number): Promise<void> {
  return apiDeleteAuth(`/user/builders/${id}`);
}

// Fixture notes
export interface FixtureNoteAPI {
  id: number;
  fixture_id: number;
  content: string;
  created_at: string;
  updated_at: string;
}

export async function getFixtureNotes(fixture_id?: number): Promise<FixtureNoteAPI[]> {
  const params = fixture_id ? `?fixture_id=${fixture_id}` : '';
  return apiGetAuth<FixtureNoteAPI[]>(`/user/notes${params}`);
}

export async function getFixtureNote(fixture_id: number): Promise<FixtureNoteAPI> {
  return apiGetAuth<FixtureNoteAPI>(`/user/notes/${fixture_id}`);
}

export async function saveFixtureNote(fixture_id: number, content: string): Promise<FixtureNoteAPI> {
  return apiPostAuth<FixtureNoteAPI>('/user/notes', { fixture_id, content });
}

export async function deleteFixtureNote(fixture_id: number): Promise<void> {
  return apiDeleteAuth(`/user/notes/${fixture_id}`);
}

// ============================================================
// Alerts API Functions (Phase 6)
// ============================================================

export interface AlertPreferences {
  lineup_alerts: boolean;
  builder_alerts: boolean;
  goal_alerts: boolean;
  card_alerts: boolean;
  foul_warnings: boolean;
  milestone_alerts: boolean;
  push_enabled: boolean;
}

export interface UserAlert {
  id: number;
  alert_type: string;
  title: string;
  message: string | null;
  fixture_id: number | null;
  player_id: number | null;
  builder_id: number | null;
  payload: Record<string, unknown> | null;
  is_read: boolean;
  is_dismissed: boolean;
  created_at: string;
}

export interface AlertListResponse {
  alerts: UserAlert[];
  total_count: number;
  unread_count: number;
}

export interface BookmakerLinkInfo {
  slug: string;
  name: string;
  deep_link_supported: boolean;
}

export interface BookmakerLink {
  url: string;
  bookmaker_name: string;
  bookmaker_slug: string;
  deep_link_supported: boolean;
  affiliate_id: string;
}

export async function getAlertPreferences(): Promise<AlertPreferences> {
  return apiGetAuth<AlertPreferences>('/alerts/preferences');
}

export async function updateAlertPreferences(
  prefs: Partial<AlertPreferences>
): Promise<AlertPreferences> {
  return apiPutAuth<AlertPreferences>('/alerts/preferences', prefs);
}

export async function getAlerts(
  params?: { alert_type?: string; unread_only?: boolean; limit?: number; offset?: number }
): Promise<AlertListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.alert_type) searchParams.set('alert_type', params.alert_type);
  if (params?.unread_only) searchParams.set('unread_only', 'true');
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return apiGetAuth<AlertListResponse>(`/alerts${query ? `?${query}` : ''}`);
}

export async function getUnreadAlertCount(): Promise<{ unread_count: number }> {
  return apiGetAuth<{ unread_count: number }>('/alerts/unread-count');
}

export async function markAlertsRead(alertIds?: number[]): Promise<{ status: string }> {
  return apiPostAuth<{ status: string }>('/alerts/mark-read', { alert_ids: alertIds });
}

export async function dismissAlert(alertId: number): Promise<{ status: string }> {
  return apiPutAuth<{ status: string }>(`/alerts/${alertId}/dismiss`, {});
}

export async function dismissAllAlerts(): Promise<{ status: string }> {
  return apiPostAuth<{ status: string }>('/alerts/dismiss-all', {});
}

export async function getSupportedBookmakers(): Promise<{ bookmakers: BookmakerLinkInfo[] }> {
  return apiGet<{ bookmakers: BookmakerLinkInfo[] }>('/alerts/bookmakers');
}

export async function getBookmakerLinks(
  params?: { fixture_id?: number; market_type?: string; selection?: string }
): Promise<{ links: BookmakerLink[] }> {
  const searchParams = new URLSearchParams();
  if (params?.fixture_id) searchParams.set('fixture_id', String(params.fixture_id));
  if (params?.market_type) searchParams.set('market_type', params.market_type);
  if (params?.selection) searchParams.set('selection', params.selection);
  const query = searchParams.toString();
  return apiGet<{ links: BookmakerLink[] }>(`/alerts/bookmaker-links${query ? `?${query}` : ''}`);
}

// ============================================================
// Time-Split / Tempo API Functions (Phase 5)
// ============================================================

export async function getFixtureTimeSplits(fixtureId: number): Promise<FixtureTimeSplitContext> {
  return apiGet<FixtureTimeSplitContext>(`/fixtures/${fixtureId}/time-splits`);
}

// ============================================================
// Cards Tab API Functions
// ============================================================

export async function getFixtureCardsContext(fixtureId: number): Promise<CardsContextResponse> {
  return apiGet<CardsContextResponse>(`/fixtures/${fixtureId}/cards-context`);
}

// ============================================================
// Admin API Functions
// ============================================================

// Legacy cleanup result type
export interface CleanupResult {
  dry_run: boolean;
  records_to_delete: number;
  size_freed_mb?: number;
}

export interface ConfigValue {
  key: string;
  value: string | null;
  found: boolean;
}

export interface AdminMarket {
  id: number;
  api_bet_id: number;
  name: string;
  slug: string;
  category: string;
  has_line: boolean;
  has_player: boolean;
  is_active: boolean;
}

// CLV types
export interface CLVStats {
  count: number;
  avg_clv: number | null;
  median_clv: number | null;
  std_clv: number | null;
  positive_pct: number | null;
  min_clv: number | null;
  max_clv: number | null;
  by_confidence?: Record<string, { count: number; avg_clv: number; positive_pct: number }>;
  by_market?: Record<string, { count: number; avg_clv: number; positive_pct: number }>;
  interpretation?: string;
  error?: string;
}

export interface CLVPrediction {
  id: number;
  fixture_id: number;
  fixture_date: string | null;
  market_type: string;
  line: number | null;
  outcome: string;
  predicted_prob: number;
  pinnacle_odds: number | null;
  pinnacle_implied_prob: number | null;
  clv: number | null;
  clv_pct: number | null;
  actual_outcome: boolean | null;
  brier_score: number | null;
}

export interface CLVPredictionsResponse {
  total: number;
  offset: number;
  limit: number;
  predictions: CLVPrediction[];
}

// Config management
export async function getConfigValue(key: string): Promise<ConfigValue> {
  return apiGetAuth<ConfigValue>(`/admin/config/${key}`);
}

export async function updateConfigValue(key: string, value: string): Promise<ConfigValue> {
  return apiPutAuth<ConfigValue>(`/admin/config/${key}`, { value });
}

// Markets management
export async function getAdminMarkets(includeInactive = false): Promise<AdminMarket[]> {
  return apiGetAuth<AdminMarket[]>(`/admin/markets?include_inactive=${includeInactive}`);
}

export async function activateMarket(apiBetId: number): Promise<AdminMarket> {
  return apiPutAuth<AdminMarket>(`/admin/markets/${apiBetId}/activate`, {});
}

export async function deactivateMarket(apiBetId: number): Promise<AdminMarket> {
  return apiPutAuth<AdminMarket>(`/admin/markets/${apiBetId}/deactivate`, {});
}

// CLV tracking
export async function getCLVReport(params?: {
  model_version?: number;
  market_type?: string;
}): Promise<CLVStats> {
  const searchParams = new URLSearchParams();
  if (params?.model_version) searchParams.set('model_version', String(params.model_version));
  if (params?.market_type) searchParams.set('market_type', params.market_type);
  const query = searchParams.toString();
  return apiGetAuth<CLVStats>(`/admin/clv-report${query ? `?${query}` : ''}`);
}

export async function getCLVPredictions(params?: {
  limit?: number;
  offset?: number;
  min_clv?: number;
  max_clv?: number;
}): Promise<CLVPredictionsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  if (params?.min_clv !== undefined) searchParams.set('min_clv', String(params.min_clv));
  if (params?.max_clv !== undefined) searchParams.set('max_clv', String(params.max_clv));
  const query = searchParams.toString();
  return apiGetAuth<CLVPredictionsResponse>(`/admin/clv-predictions${query ? `?${query}` : ''}`);
}

// ============================================================
// Database Operations API Functions (Phase 1)
// ============================================================

import type {
  DbStats,
  SlowQueriesResponse,
  DataFreshnessResponse,
  CleanupStats,
  GetSlowQueriesParams,
  IndexUsageResponse,
  GetIndexUsageParams,
  ConnectionPoolStatsResponse,
  ConnectionPoolHistoryResponse,
  CacheStatsResponse,
  CacheCleanupResponse,
  CacheClearResponse,
  QueryPerformanceByTypeResponse,
  GetQueryPerformanceByTypeParams,
  ExplainAnalyzeRequest,
  ExplainAnalyzeResponse,
  RetentionPolicy,
  CleanupPreviewResponse,
  CleanupExecuteRequest,
  CleanupExecuteResponse,
  CleanupHistoryResponse,
  GetCleanupHistoryParams,
  JobsResponse,
  JobHistoryResponse,
  GetJobHistoryParams,
  JobLogsResponse,
  JobTriggerResponse,
} from './hooks/admin/types/database';

/**
 * Get comprehensive database statistics for monitoring
 */
export async function getDbStats(): Promise<DbStats> {
  return apiGetAuth<DbStats>('/admin/db-stats');
}

/**
 * Get slow queries from pg_stat_statements
 */
export async function getSlowQueries(params?: GetSlowQueriesParams): Promise<SlowQueriesResponse> {
  const searchParams = new URLSearchParams();
  if (params?.hours) searchParams.set('hours', String(params.hours));
  if (params?.threshold_ms) searchParams.set('threshold_ms', String(params.threshold_ms));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  return apiGetAuth<SlowQueriesResponse>(`/admin/db-stats/slow-queries${query ? `?${query}` : ''}`);
}

/**
 * Get query performance statistics grouped by query type
 */
export async function getQueryPerformanceByType(params?: GetQueryPerformanceByTypeParams): Promise<QueryPerformanceByTypeResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  return apiGetAuth<QueryPerformanceByTypeResponse>(`/admin/db-stats/query-performance-by-type${query ? `?${query}` : ''}`);
}

/**
 * Get data freshness status for all data types
 */
export async function getDataFreshness(): Promise<DataFreshnessResponse> {
  return apiGetAuth<DataFreshnessResponse>('/admin/db-stats/freshness');
}

/**
 * Run cleanup operation (dry-run or actual)
 */
export async function cleanupOddsHistory(dry_run: boolean = true): Promise<CleanupStats> {
  const searchParams = new URLSearchParams();
  searchParams.set('dry_run', String(dry_run));
  return apiPostAuth<CleanupStats>(`/admin/cleanup-odds-history?${searchParams.toString()}`, {});
}

/**
 * Get connection pool statistics
 */
export async function getConnectionPoolStats(): Promise<ConnectionPoolStatsResponse> {
  return apiGetAuth<ConnectionPoolStatsResponse>('/admin/db-stats/connection-pool');
}

/**
 * Get connection pool statistics history
 */
export async function getConnectionPoolHistory(hours: number = 24): Promise<ConnectionPoolHistoryResponse> {
  return apiGetAuth<ConnectionPoolHistoryResponse>(`/admin/db-stats/connection-pool/history?hours=${hours}`);
}

/**
 * Get cache statistics (in-memory or Redis)
 */
export async function getCacheStats(): Promise<CacheStatsResponse> {
  return apiGetAuth<CacheStatsResponse>('/admin/db-stats/cache');
}

/**
 * Clean up expired cache entries
 */
export async function cleanupCache(): Promise<CacheCleanupResponse> {
  return apiPostAuth<CacheCleanupResponse>('/admin/db-stats/cache/cleanup', {});
}

/**
 * Clear all caches completely
 */
export async function clearCache(): Promise<CacheClearResponse> {
  return apiPostAuth<CacheClearResponse>('/admin/db-stats/cache/clear', {});
}

/**
 * Get index usage statistics
 */
export async function getIndexUsage(params?: GetIndexUsageParams): Promise<IndexUsageResponse> {
  const searchParams = new URLSearchParams();
  if (params?.table_name) searchParams.set('table_name', params.table_name);
  if (params?.min_size_mb !== undefined) searchParams.set('min_size_mb', String(params.min_size_mb));
  if (params?.unused_only !== undefined) searchParams.set('unused_only', String(params.unused_only));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  return apiGetAuth<IndexUsageResponse>(`/admin/db-stats/index-usage${query ? `?${query}` : ''}`);
}

/**
 * Analyze a SQL query using EXPLAIN ANALYZE
 */
export async function explainAnalyzeQuery(request: ExplainAnalyzeRequest): Promise<ExplainAnalyzeResponse> {
  return apiPostAuth<ExplainAnalyzeResponse>('/admin/db-stats/explain', request);
}

/**
 * Phase 4: Enhanced Cleanup API Functions
 */

/**
 * Preview cleanup operation with given retention policy (dry-run)
 */
export async function previewCleanup(retentionPolicy: RetentionPolicy): Promise<CleanupPreviewResponse> {
  return apiPostAuth<CleanupPreviewResponse>('/admin/cleanup/preview', retentionPolicy);
}

/**
 * Execute cleanup operation with given retention policy
 */
export async function executeCleanup(request: CleanupExecuteRequest): Promise<CleanupExecuteResponse> {
  return apiPostAuth<CleanupExecuteResponse>('/admin/cleanup/execute', request);
}

/**
 * Get cleanup operation history with pagination and filtering
 */
export async function getCleanupHistory(params?: GetCleanupHistoryParams): Promise<CleanupHistoryResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  if (params?.trigger_type) searchParams.set('trigger_type', params.trigger_type);
  if (params?.status) searchParams.set('status', params.status);
  const query = searchParams.toString();
  return apiGetAuth<CleanupHistoryResponse>(`/admin/cleanup/history${query ? `?${query}` : ''}`);
}

/**
 * Phase 5: Jobs Management API Functions
 */

/**
 * Get all scheduled jobs with their current status
 */
export async function getAllJobs(): Promise<JobsResponse> {
  return apiGetAuth<JobsResponse>('/admin/jobs');
}

/**
 * Get execution history for a specific job
 */
export async function getJobHistory(params: GetJobHistoryParams): Promise<JobHistoryResponse> {
  const searchParams = new URLSearchParams();
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.offset) searchParams.set('offset', String(params.offset));
  const query = searchParams.toString();
  return apiGetAuth<JobHistoryResponse>(`/admin/jobs/${params.job_id}/history${query ? `?${query}` : ''}`);
}

/**
 * Get logs for a specific job execution
 */
export async function getJobLogs(job_id: string, execution_id: number): Promise<JobLogsResponse> {
  return apiGetAuth<JobLogsResponse>(`/admin/jobs/${job_id}/logs/${execution_id}`);
}

/**
 * Trigger a job to run manually
 */
export async function triggerJob(job_id: string): Promise<JobTriggerResponse> {
  return apiPostAuth<JobTriggerResponse>(`/admin/jobs/${job_id}/trigger`, {});
}

// ============================================================
// Player Profile API Functions
// ============================================================

import type { PlayerProfile, FixtureLite } from './types';
import type { ExtendedPlayerProfile } from './components/PlayerProfilePage';

/**
 * Get player profile with aggregated season stats.
 * Used for displaying player info when clicking on player names across the app.
 */
export async function getPlayerProfile(playerId: number): Promise<PlayerProfile> {
  return apiGet<PlayerProfile>(`/players/${playerId}`);
}

/**
 * Get extended player profile with all data for Player Profile page.
 * Includes: injury status, percentiles, trophies, transfers, market value history.
 */
export async function getExtendedPlayerProfile(
  playerId: number,
  userId?: string
): Promise<ExtendedPlayerProfile> {
  const params = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
  return apiGet<ExtendedPlayerProfile>(`/players/${playerId}/extended${params}`);
}

/**
 * Player match history types
 */
export type PlayerMatchTeam = {
  id: number;
  name: string;
  crest_url?: string | null;
};

export type PlayerMatch = {
  fixture_id: number;
  kickoff_utc: string;
  status: string;
  home_team: PlayerMatchTeam;
  away_team: PlayerMatchTeam;
  home_score?: number | null;
  away_score?: number | null;
  league_name?: string | null;
  league_logo?: string | null;
  player_team_id: number;
  minutes?: number | null;
  rating?: number | null;
  goals: number;
  assists: number;
  yellows: number;
  reds: number;
  is_starter?: boolean | null;
};

export type UpcomingFixture = {
  fixture_id: number;
  kickoff_utc: string;
  status: string;
  home_team: PlayerMatchTeam;
  away_team: PlayerMatchTeam;
  league_name?: string | null;
  player_team_id: number;
};

export type PlayerMatchesResponse = {
  player_id: number;
  upcoming: UpcomingFixture[];
  matches: PlayerMatch[];
  total: number;
};

/**
 * Get player's match history with their stats for each match.
 */
export async function getPlayerMatches(
  playerId: number,
  limit: number = 20,
  offset: number = 0
): Promise<PlayerMatchesResponse> {
  return apiGet<PlayerMatchesResponse>(`/players/${playerId}/matches?limit=${limit}&offset=${offset}`);
}

/**
 * Player season stats types
 */
export type PlayerSeasonStats = {
  season: string;
  season_year: number;
  league_id: number;
  league_name: string;
  league_logo?: string | null;
  league_country?: string | null;  // e.g., "England", "World"
  team_id?: number | null;
  team_name?: string | null;
  team_crest?: string | null;
  matches: number;
  started: number;
  minutes: number;
  goals: number;
  assists: number;
  rating?: number | null;
  shots: number;
  shots_on_target: number;
  passes: number;
  key_passes: number;
  pass_accuracy?: number | null;
  tackles: number;
  interceptions: number;
  blocks: number;
  clearances: number;
  duels_total: number;
  duels_won: number;
  aerials_total: number;
  aerials_won: number;
  dribbles_attempts: number;
  dribbles_success: number;
  yellows: number;
  reds: number;
  yellow_red: number;  // Second yellow leading to red
  fouls_committed: number;
  fouls_drawn: number;
  dribbles_past: number;  // Times dribbled past (defensive)
  saves: number;
  goals_conceded: number;
  penalty_saved: number;
  penalty_won: number;
  penalty_committed: number;
  penalty_scored: number;
  penalty_missed: number;
  // Appearance breakdown
  position?: string | null;  // Position played in this competition
  captain: number;  // Games as captain
  sub_in: number;  // Times substituted on
  sub_out: number;  // Times substituted off
  bench: number;  // Times on bench (unused sub)
};

export type PlayerStatsResponse = {
  player_id: number;
  seasons: PlayerSeasonStats[];
};

/**
 * Get player's stats broken down by season and league.
 */
export async function getPlayerStats(playerId: number): Promise<PlayerStatsResponse> {
  return apiGet<PlayerStatsResponse>(`/players/${playerId}/stats`);
}

/**
 * Player career types
 */
export type CareerSeasonEntry = {
  team_id: number;
  team_name: string;
  team_crest?: string | null;
  season: string;
  season_year: number;
  is_national_team: boolean;
  matches: number;
  goals: number;
  assists: number;
  rating?: number | null;
};

export type CareerClubEntry = {
  team_id: number;
  team_name: string;
  team_crest?: string | null;
  transfer_type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_national_team: boolean;
  matches: number;
  goals: number;
};

export type PlayerCareerResponse = {
  player_id: number;
  // Club career
  club_seasons: CareerSeasonEntry[];
  clubs: CareerClubEntry[];
  // National team career
  national_seasons: CareerSeasonEntry[];
  national_teams: CareerClubEntry[];
};

/**
 * Get player's career history - seasons and clubs.
 */
export async function getPlayerCareer(playerId: number): Promise<PlayerCareerResponse> {
  return apiGet<PlayerCareerResponse>(`/players/${playerId}/career`);
}

// Real-time stats from API-Football (1 API call)
export interface RealTimeLeagueStats {
  league_id: number;
  league_name: string;
  league_logo: string | null;
  league_country: string | null;  // e.g., "England", "World"
  team_id: number | null;
  team_name: string | null;
  team_crest: string | null;
  is_national_team: boolean;  // True for international caps
  // Game info
  matches: number;
  started: number;
  minutes: number;
  position: string | null;  // Position played in this competition
  captain: boolean;  // Captain appearances
  // Substitutes
  sub_in: number;  // Times subbed on
  sub_out: number;  // Times subbed off
  bench: number;  // Times on bench but didn't play
  // Goals & assists
  goals: number;
  assists: number;
  rating: number | null;
  // Shots
  shots: number;
  shots_on_target: number;
  // Passes
  passes: number;
  key_passes: number;
  pass_accuracy: number | null;
  // Defense
  tackles: number;
  blocks: number;  // Shot blocks
  interceptions: number;
  // Duels
  duels_total: number;
  duels_won: number;
  // Dribbles
  dribbles_success: number;
  dribbles_attempts: number;
  dribbles_past: number;  // Times dribbled past (defenders)
  // Cards & fouls
  yellows: number;
  yellow_red: number;  // Second yellow leading to red
  reds: number;
  fouls_committed: number;
  fouls_drawn: number;
  // Penalties
  penalty_won: number;
  penalty_committed: number;
  penalty_scored: number;
  penalty_missed: number;
  // Goalkeeper
  saves: number;
  goals_conceded: number;
  penalty_saved: number;
}

export interface PlayerBirth {
  date: string | null;
  place: string | null;
  country: string | null;
}

export interface RealTimeStatsResponse {
  player_id: number;
  api_player_id: number;
  name: string;
  firstname: string | null;
  lastname: string | null;
  photo_url: string | null;
  age: number | null;
  nationality: string | null;
  birth: PlayerBirth | null;
  height: string | null;  // in cm
  weight: string | null;  // in kg
  injured: boolean;
  season: number;
  leagues: RealTimeLeagueStats[];
  fetched_at: string;
}

/**
 * Get real-time player stats from API-Football.
 * Cost: 1 API call per request.
 * Use this for fresh stats without waiting for scheduled ingestion.
 */
export async function getPlayerRealtimeStats(playerId: number): Promise<RealTimeStatsResponse> {
  return apiGet<RealTimeStatsResponse>(`/players/${playerId}/realtime-stats`);
}

// ============================================================
// Team Fixtures API Functions
// ============================================================

export interface PaginatedFixturesResponse {
  past_fixtures: FixtureLite[];
  upcoming_fixtures: FixtureLite[];
  has_more_past: boolean;
  has_more_future: boolean;
}

export interface PaginatedFixturesParams {
  direction?: 'past' | 'future' | 'both';
  limit?: number;
  beforeDate?: string;
  afterDate?: string;
}

/**
 * Get team fixtures with pagination support for infinite scroll.
 * - direction="both": Initial load, returns fixtures centered around now
 * - direction="past": Load more past fixtures before beforeDate
 * - direction="future": Load more future fixtures after afterDate
 */
export async function getTeamFixturesPaginated(
  teamId: number,
  params: PaginatedFixturesParams = {}
): Promise<PaginatedFixturesResponse> {
  const searchParams = new URLSearchParams();
  if (params.direction) searchParams.set('direction', params.direction);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.beforeDate) searchParams.set('before_date', params.beforeDate);
  if (params.afterDate) searchParams.set('after_date', params.afterDate);
  const query = searchParams.toString();
  return apiGet<PaginatedFixturesResponse>(`/teams/${teamId}/fixtures/paginated${query ? `?${query}` : ''}`);
}
